import React, { useState, useEffect, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../contexts/LanguageContext';

const initialFilters = {
    chassisNo: '',
    brand: '',
    model: '',
    matnr: '',
    maktx: '',
    sellable: null
};

const VehicleStockFilter = ({ filters, onApplyFilters, onReset }) => {
    const { th } = useLanguage();
    const [filterOpen, setFilterOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState({ ...initialFilters, ...filters });

    useEffect(() => {
        setLocalFilters({ ...initialFilters, ...filters });
    }, [filters]);

    const toggleOpen = useCallback(() => setFilterOpen((prev) => !prev), []);

    const handleChange = (field, value) => {
        setLocalFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleApply = () => {
        onApplyFilters(localFilters);
    };

    const handleReset = () => {
        onReset();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleApply();
    };

    const sellableOptions = [
        { label: th('vehicleSalesMyVehicleStock.sellable.unspecified'), value: 0 },
        { label: th('vehicleSalesMyVehicleStock.sellable.notForSale'), value: 1 },
        { label: th('vehicleSalesMyVehicleStock.sellable.forSale'), value: 2 }
    ];

    return (
        <div className="generic-table-filter">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button
                        icon="pi pi-filter"
                        label={th('filter.advancedFilter')}
                        onClick={toggleOpen}
                        outlined={!filterOpen}
                    />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">
                        {th('filter.advancedFilters')}
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.chassisNo')}
                                </label>
                                <InputText
                                    value={localFilters.chassisNo}
                                    onChange={(e) => handleChange('chassisNo', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.chassisNoPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.brand')}
                                </label>
                                <InputText
                                    value={localFilters.brand}
                                    onChange={(e) => handleChange('brand', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.brandPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.model')}
                                </label>
                                <InputText
                                    value={localFilters.model}
                                    onChange={(e) => handleChange('model', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.modelPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.matnr')}
                                </label>
                                <InputText
                                    value={localFilters.matnr}
                                    onChange={(e) => handleChange('matnr', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.matnrPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.maktx')}
                                </label>
                                <InputText
                                    value={localFilters.maktx}
                                    onChange={(e) => handleChange('maktx', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.maktxPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6 lg:col-3">
                            <div className="field">
                                <label className="block mb-2 text-sm">
                                    {th('vehicleSalesMyVehicleStock.columns.sellable')}
                                </label>
                                <Dropdown
                                    value={localFilters.sellable}
                                    options={sellableOptions}
                                    onChange={(e) => handleChange('sellable', e.value)}
                                    className="w-full"
                                    placeholder={th('vehicleSalesMyVehicleStock.filter.sellablePlaceholder')}
                                    showClear
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3 mb-3 gap-2">
                        <Button
                            label={th('filter.reset')}
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={handleReset}
                        />
                        <Button
                            label={th('filter.apply')}
                            icon="pi pi-search"
                            onClick={handleApply}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default VehicleStockFilter;
